import { type CSSProperties } from "react";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";

interface Props {
  term: string;
  count: number;
  current: number;
  onTermChange: (v: string) => void;
  onStep: (dir: 1 | -1) => void;
  onClose: () => void;
}

/** The in-memo find bar (Ctrl+F / arrive-via-search). Presentational — all state
 * lives in useMemoFind. The `.flow-memo-find` class keeps the bar's own text out
 * of the highlight walker. */
export function MemoFindBar({ term, count, current, onTermChange, onStep, onClose }: Props) {
  return (
    <div className="flow-memo-find" style={barStyle} role="search">
      <Icon name="search" size={14} color="var(--ink-3)" />
      <input
        autoFocus
        value={term}
        onChange={(e) => onTermChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onStep(e.shiftKey ? -1 : 1);
          } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          }
        }}
        placeholder={t.memoFindPlaceholder}
        style={inputStyle}
        aria-label={t.memoFindPlaceholder}
      />
      <span style={countStyle}>
        {count ? `${current + 1} ${t.memoFindOf} ${count}` : `0 ${t.memoFindOf} 0`}
      </span>
      <button
        type="button"
        onClick={() => onStep(-1)}
        disabled={count === 0}
        aria-label={t.memoFindPrev}
        style={iconBtnStyle}
      >
        <Icon name="chevUp" size={15} color="var(--ink-2)" />
      </button>
      <button
        type="button"
        onClick={() => onStep(1)}
        disabled={count === 0}
        aria-label={t.memoFindNext}
        style={iconBtnStyle}
      >
        <Icon name="chevDown" size={15} color="var(--ink-2)" />
      </button>
      <button type="button" onClick={onClose} aria-label={t.memoFindClose} style={iconBtnStyle}>
        <Icon name="x" size={15} color="var(--ink-2)" />
      </button>
    </div>
  );
}

const barStyle: CSSProperties = {
  position: "sticky",
  top: 12,
  zIndex: 40,
  marginLeft: "auto",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px 6px 12px",
  width: "fit-content",
  maxWidth: "calc(100vw - 24px)",
  background: "var(--surface-elev)",
  border: "1px solid var(--hairline-strong)",
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(13,62,99,0.14)",
};

const inputStyle: CSSProperties = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 13,
  color: "var(--ink-1)",
  width: 180,
  maxWidth: "45vw",
  minWidth: 0,
  fontFamily: "inherit",
};

const countStyle: CSSProperties = {
  fontSize: 11.5,
  color: "var(--ink-3)",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  minWidth: 56,
  textAlign: "right",
};

const iconBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  border: "none",
  borderRadius: 6,
  background: "transparent",
  cursor: "pointer",
};
